import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { formatApiError } from "@/lib/api";
import { AlertCircle, ArrowRight } from "lucide-react";

export default function Login() {
  const { t } = useTranslation();
  const { login, user } = useAuth();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) {
    const redirectTo = location.state?.from?.pathname || "/app";
    return <Navigate to={redirectTo} replace />;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const errCode = typeof detail === "object" && detail ? detail.error : null;
      if (errCode === "tenant_suspended") {
        setError({ kind: "suspended", company: detail.company_name });
      } else if (errCode === "tenant_deleted") {
        setError({ kind: "deleted" });
      } else if (errCode === "account_disabled") {
        setError(t("login.errorInvalid"));
      } else if (status === 401 || status === 403) {
        setError(typeof detail === "string" ? detail : t("login.errorInvalid"));
      } else {
        setError(formatApiError(err, t("login.errorGeneric")));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="login-page">
      {/* Form side */}
      <div className="flex w-full flex-col justify-between px-6 py-10 sm:px-12 md:w-1/2 md:px-16 lg:px-24">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-900 text-white text-sm font-semibold">
              IP
            </div>
            <div className="font-heading text-base font-medium tracking-tight text-slate-900">
              {t("appName")}
            </div>
          </div>
          <LanguageSwitcher testId="login-lang-switcher" />
        </div>

        <div className="mx-auto w-full max-w-sm py-12">
          <div className="mb-10">
            <h1 className="font-heading text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl">
              {t("login.title")}
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-slate-500">
              {t("login.subtitle")}
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {t("login.email")}
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("login.emailPlaceholder")}
                data-testid="login-email"
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {t("login.password")}
              </Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("login.passwordPlaceholder")}
                data-testid="login-password"
                className="h-11"
              />
            </div>

            {error && (
              <div
                role="alert"
                data-testid="login-error"
                className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                {typeof error === "object" && error?.kind === "suspended" ? (
                  <span>
                    {t("login.suspended", { company: error.company || "This workspace" })}{" "}
                    <a
                      href="mailto:support@nordicscandiskroyal.se?subject=Workspace%20suspended"
                      className="font-medium underline"
                      data-testid="login-contact-support"
                    >
                      {t("login.contactSupport")}
                    </a>
                  </span>
                ) : typeof error === "object" && error?.kind === "deleted" ? (
                  <span>{t("login.deleted")}</span>
                ) : (
                  <span>{error}</span>
                )}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              className="h-11 w-full gap-2 bg-slate-900 text-white hover:bg-slate-800"
            >
              {submitting ? t("login.submitting") : t("login.submit")}
              <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
            </Button>
          </form>
        </div>

        <div className="text-xs text-slate-400">{t("login.footer")}</div>
      </div>

      {/* Marketing / image side */}
      <div className="relative hidden w-1/2 overflow-hidden bg-slate-900 md:block" data-testid="login-marketing">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-70"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1610497254766-c6a51afa9b64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMG1pbmltYWwlMjBhcmNoaXRlY3R1cmFsJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3Nzc3OTYxOTh8MA&ixlib=rb-4.1.0&q=85)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-slate-900/40 to-transparent" />
        <div className="relative flex h-full flex-col justify-end p-12 text-white">
          <div className="max-w-md space-y-4">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">
              InvoicePro
            </div>
            <h2 className="font-heading text-3xl font-medium leading-tight tracking-tight">
              {t("login.marketingHeadline")}
            </h2>
            <p className="text-sm leading-relaxed text-white/80">
              {t("login.marketingBody")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
